import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import { getMarketplaceState } from './marketplaceApi';
import { MARKETPLACE_DONE_PATH, MARKETPLACE_RUN_PATH } from './marketplaceLinks';

/**
 * `/marketplace` entry — a pure dispatcher, it renders no page of its own.
 *
 * There is no in-app marketing landing any more: a logged-out visitor goes
 * straight to the gated run flow, where MarketplaceGate shows signup (it lists
 * /marketplace/run in ENTRY_PATHS, so this is the signup panel, not the
 * deep-link login variant). The public marketing page lives off-app at
 * blue-pill.ai/marketplace.
 *
 * Signed-in marketplace users have NO dashboard — they're routed by where they
 * are in the journey: a free user (free study unused) lands on the free study
 * flow, a returning user who has spent their free study lands on the "what would
 * you like to do next?" conversion screen (so full-access is always one step
 * away). Paid studies do NOT affect this — free_study_used counts only free ones
 * (models_postgres/marketplace_studies.has_used_free_study), so a paid customer
 * is routed by whether they ever used their free study.
 */
const MarketplaceHome: React.FC = () => {
  const { isAuthenticated, loading } = useMarketplaceAuth();
  const [freeUsed, setFreeUsed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    // Fail open to the free study page if the request rejects OR hangs (a stalled
    // /me/state must not leave the user on a blank screen forever).
    const timeout = setTimeout(() => {
      if (active) setFreeUsed(false);
    }, 10000);
    getMarketplaceState()
      .then((s) => {
        if (active) setFreeUsed(s.free_study_used);
      })
      .catch(() => {
        if (active) setFreeUsed(false);
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [isAuthenticated]);

  if (loading) return null; // brief — avoids a redirect flash before auth resolves
  if (!isAuthenticated) return <Navigate to={MARKETPLACE_RUN_PATH} replace />;
  if (freeUsed === null) {
    // Resolving free-vs-paid routing — show a spinner rather than a blank screen.
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  return <Navigate to={freeUsed ? MARKETPLACE_DONE_PATH : MARKETPLACE_RUN_PATH} replace />;
};

export default MarketplaceHome;
